import { styled } from 'styled-components';
import { Navigate, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React, { FC, Suspense, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { Axios } from '@idk-web/core-utils';
import {
  useVisibility,
  useQueryParams,
  ParamTypes,
  Box,
  useDialogs,
  AcknowledgeDialog,
  ErrorBoundary,
} from '@idk-web/core-ui';
import { privateLoginStatus } from '@/state/privateAuth';
import { SentryRoutes } from '@/sentry';
import { usePrivateUser } from '@/hooks/private/usePrivateUser';
import { usePrivateLogin } from '@/hooks/private/login/usePrivateLogin';
import routes from '@/components/private/routes';
import UnauthenticatedRoute from '@/components/private/UnauthenticatedRoute';
import TermsOfServiceApproval from '@/components/private/TermsOfServiceApproval';
import Layout from '@/components/private/Layout';
import AuthenticatedRoute from '@/components/private/AuthenticatedRoute';
import ScheduledMaintenanceMessage from '@/components/common/ScheduledMaintenanceMessage';
import BrowserSupportMessage from '@/components/common/BrowserSupportMessage';

const ErrorScreen = React.lazy(() => import('@/components/error/ErrorScreen'));
const LoginCallback = React.lazy(
  () => import('@/components/common/auth/LoginCallback'),
);
const LoginScreen = React.lazy(
  () => import('@/components/private/login/LoginScreen'),
);
const EmailValidationScreen = React.lazy(
  () => import('@/components/private/email/EmailValidationScreen'),
);
const EmailRequestScreen = React.lazy(
  () => import('@/components/private/email/EmailRequestScreen'),
);
const NewAuthRequestScreen = React.lazy(
  () => import('@/components/private/new/NewAuthRequestScreen'),
);
// const NewSignRequestScreen = React.lazy(
//   () => import('@/components/private/new/NewSignRequestScreen'),
// );
const HistoryScreen = React.lazy(
  () => import('@/components/private/history/HistoryScreen'),
);
const AuthScreen = React.lazy(
  () => import('@/components/private/auth/AuthScreen'),
);
// const SignScreen = React.lazy(
//   () => import('@/components/private/sign/SignScreen'),
// );
const ProfileScreen = React.lazy(
  () => import('@/components/private/profile/ProfileScreen'),
);

const Container = styled(Box).attrs({
  direction: 'vertical',
})``;

const queryTypes = {
  success: ParamTypes.string(),
};

const PrivateApp: FC = () => {
  const { t } = useTranslation();
  const { params, setParam } = useQueryParams(queryTypes);
  const dialog = useDialogs();
  const auth = usePrivateLogin();
  const user = usePrivateUser();
  const loginStatus = useAtomValue(privateLoginStatus);
  const visibility = useVisibility();

  useEffect(() => {
    if (loginStatus === 'LOGGED_IN' && visibility === 'visible') {
      (async () => {
        try {
          await user.reload();
        } catch (e) {
          if (
            Axios.isError(e) &&
            e.response &&
            Axios.is4xxResponse(e.response)
          ) {
            await auth.logout();
          } else {
            console.error('Failed to reload private user', e);
          }
        }
      })();
    }
  }, [loginStatus, visibility]);

  useEffect(() => {
    if (params.success !== null) {
      dialog.open((props) => (
        <AcknowledgeDialog
          {...props}
          header={t('private.topup.success.header')}
          text={t('private.topup.success.text')}
          onClose={() => {
            setParam('success', null);
            props.onClose?.();
          }}
        />
      ));
    }
  }, []);

  return (
    <Container>
      <ErrorBoundary fallback={ErrorScreen}>
        <BrowserSupportMessage />
        <ScheduledMaintenanceMessage />
        <Layout>
          <Suspense fallback={null}>
            <SentryRoutes>
              <Route
                path={routes.loginCallback}
                element={<LoginCallback redirect={routes.login} />}
              />
              <Route
                path={routes.validateEmail}
                element={<EmailValidationScreen />}
              />
              <Route
                path={routes.login}
                element={
                  <UnauthenticatedRoute>
                    <LoginScreen />
                  </UnauthenticatedRoute>
                }
              />
              <Route
                path={routes.newAuthRequest}
                element={
                  <AuthenticatedRoute>
                    {loginStatus === 'LOGGED_IN' && !user?.hasValidatedEmail ? (
                      <EmailRequestScreen />
                    ) : loginStatus === 'LOGGED_IN' &&
                      !user.termsOfServiceAccepted ? (
                      // Users are required to accept the ToS
                      <TermsOfServiceApproval />
                    ) : (
                      <NewAuthRequestScreen />
                    )}
                  </AuthenticatedRoute>
                }
              />
              {/*<Route
                path={routes.newSignRequest}
                element={
                  <AuthenticatedRoute>
                    {loginStatus === 'LOGGED_IN' && !user?.hasValidatedEmail ? (
                      <EmailRequestScreen />
                    ) : loginStatus === 'LOGGED_IN' &&
                      !user.termsOfServiceAccepted ? (
                      // Users are required to accept the ToS
                      <TermsOfServiceApproval />
                    ) : (
                      <NewSignRequestScreen />
                    )}
                  </AuthenticatedRoute>
              />*/}
              <Route
                path={routes.history}
                element={
                  <AuthenticatedRoute>
                    <HistoryScreen />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path={routes.profile}
                element={
                  <AuthenticatedRoute>
                    <ProfileScreen />
                  </AuthenticatedRoute>
                }
              />
              <Route path={routes.auth} element={<AuthScreen />} />
              {/*<Route path={routes.sign} element={<SignScreen />} />*/}
              <Route
                path="*"
                element={
                  loginStatus === 'LOGGED_OUT' ? (
                    <Navigate to={routes.login} replace />
                  ) : (
                    <Navigate to={routes.newAuthRequest} replace />
                  )
                }
              />
            </SentryRoutes>
          </Suspense>
        </Layout>
      </ErrorBoundary>
    </Container>
  );
};

export default PrivateApp;
